import { store } from "@/vuex/store.js";
import { DEFAULT_EMPTY_EMAIL } from "../default_vals/enums";
import { getEmailWithAttachments } from "../../helpers/email/index";

import email_handler from "../../services/email_handler";

const constructMessageObject = (formData) => {
  return {
    id: formData.id ? formData.id : null,
    sender: {
      emailAddress: {
        address: store.state.logged_in_user.tenance_email,
      },
    },
    subject: formData.subject ? formData.subject : "",
    body: {
      contentType: "HTML",
      content: formData.body,
    },
    toRecipients: formData.recipients.map((email) => ({
      emailAddress: { address: email },
    })),
    ccRecipients: formData.cc.map((email) => ({
      emailAddress: { address: email },
    })),
    bccRecipients: formData.bcc.map((email) => ({
      emailAddress: { address: email },
    })),
  };
};

const mapRetrievedEmailToFormData = (email, empty_body = true) => {
  console.log("is called")
  let ret_obj = {...DEFAULT_EMPTY_EMAIL};
  ret_obj.recipients = email.toRecipients.map((p) => p.emailAddress.address);
  ret_obj.cc = email.ccRecipients.map((p) => p.emailAddress.address);
  ret_obj.bcc = email.bccRecipients.map((p) => p.emailAddress.address);
  ret_obj.body = empty_body ? "" : email.body.content;

  ret_obj.subject = email.subject;
  ret_obj.id = email.id;
  return ret_obj;
};

/**
 * Initialize draft email based on action type
 * @param {number} draft_type_flag - Type of draft: -1=edit, 0=new, 1=reply, 2=replyAll, 3=forward
 * @param {Object} email - Email object with id and other properties
 * @param {Array} existingAttachments - Optional: pre-loaded attachments to avoid re-downloading
 * @returns {Promise<Object>} Form data for the email composer
 */
const initDraftByType = async (draft_type_flag = 0, email, existingAttachments = null) => {
  let formData = {...DEFAULT_EMPTY_EMAIL};

  console.log("received", draft_type_flag, email);

  switch (draft_type_flag) {
    case (-1): //Continue editing the draft
      formData = mapRetrievedEmailToFormData(email, false);
      break;

    case 1: //ReplyTo
      await email_handler.createReplyDraft(email.id).then(function(resp) {
        console.log("resp from reply", resp);
        formData = mapRetrievedEmailToFormData(resp, false);
      });
      break;

    case 2: //ReplyToAll
      await email_handler.createReplyAllDraft(email.id).then(function(resp) {
        console.log("resp from reply all", resp);
        formData = mapRetrievedEmailToFormData(resp, false);
      });
      break;

    case 3: //FORWARD email
      await email_handler.createFowardEmail(email.id).then(async function(resp) {
        console.log("resp from forward", resp);
        // Use the forward draft's body but reuse existing attachments if provided
        // This avoids re-downloading all attachments which can be slow
        formData = mapRetrievedEmailToFormData(resp, false);

        if (existingAttachments && existingAttachments.length > 0) {
          // Reuse already-loaded attachments from the original email
          formData["attachments"] = existingAttachments;
        } else {
          // Fallback: fetch attachments from the new draft (slower)
          var final_email = await getEmailWithAttachments(resp.id);
          formData["attachments"] = final_email.attachments;
        }
      });
      break;

    default: // Creates new draft that is a first email.
      console.log(DEFAULT_EMPTY_EMAIL, formData);
      formData = {...DEFAULT_EMPTY_EMAIL};
      break;
  }

  formData.body = removeBodyStyle(formData.body);
  console.log(formData);
  return formData;
};

const saveEditMessageDraft = async (form_data, all_attachments) => {
  let draft_id = null;
  if (form_data.id) {
    //Check if there is draft email id already created. That means that update is in order.
    await email_handler.updateMessage(constructMessageObject(form_data));
    draft_id = form_data.id;
  } else {
    //create new draft
    await email_handler
      .createDraftMessage(constructMessageObject(form_data))
      .then((resp) => (draft_id = resp.id))
      .catch((err) => console.log(err, "dafuq happened"));
  }

  if (all_attachments.local.length > 0) {
    await localUpload(all_attachments.local, draft_id);
  }
  if (all_attachments.fw.length > 0) {
    await msUpload(all_attachments.fw, draft_id);
  }
  if (all_attachments.delete.length > 0) {
    for (let i = 0; i < all_attachments.delete.length; i++) {
      await email_handler.removeAttachmentFromEmail(
        draft_id,
        all_attachments.delete[i].id
      );
    }
  }
  return draft_id;
};

/**
 * Upload local files as attachments in parallel
 * @param {File[]} all_attachments - Array of File objects
 * @param {string} draft_id - Draft message ID
 */
const localUpload = async (all_attachments, draft_id) => {
  console.log("all_attachments (local)", all_attachments);

  // Process all uploads in parallel for better performance
  const uploadPromises = all_attachments.map(async (attachment) => {
    try {
      const base64Content = await toBase64(attachment);
      return await email_handler.addAttachmentToDraftMessage(
        draft_id,
        attachment.name,
        base64Content
      );
    } catch (error) {
      console.error(`Failed to upload attachment ${attachment.name}:`, error);
      throw error;
    }
  });

  await Promise.all(uploadPromises);
};

/**
 * Upload MS Graph files as attachments in parallel
 * @param {Object[]} all_attachments - Array of MS Graph attachment objects
 * @param {string} draft_id - Draft message ID
 */
const msUpload = async (all_attachments, draft_id) => {
  console.log("all_attachments (ms)", all_attachments);

  // Process all uploads in parallel for better performance
  const uploadPromises = all_attachments.map(async (attachment) => {
    try {
      const base64Content = await msGraphReady(attachment);
      return await email_handler.addAttachmentToDraftMessage(
        draft_id,
        attachment.name,
        base64Content
      );
    } catch (error) {
      console.error(`Failed to upload attachment ${attachment.name}:`, error);
      throw error;
    }
  });

  await Promise.all(uploadPromises);
};

const toBase64 = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]); // Extract Base64 part
    reader.onerror = (error) => reject(error);
  });
};

const msGraphReady = async (file) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(file["@microsoft.graph.downloadUrl"]);
      const blob = await response.arrayBuffer();
      //  console.log(Buffer.from(blob).toString('base64'))
      const final_val = Buffer.from(blob).toString("base64");
      resolve(final_val);
    } catch {
      reject(null);
    }
  });
};


const removeBodyStyle = (mail_body = "")=>{
  console.log("remove body", mail_body.replaceAll("color:#FFFFFF !important;", ""))
  return mail_body.replaceAll("color:#FFFFFF !important;", "")
}

export {
  constructMessageObject,
  initDraftByType,
  mapRetrievedEmailToFormData,
  saveEditMessageDraft,
};
